import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Button, Drawer, Input, Modal, Select, Space, Spin, Tooltip } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { getFacilityContext } from "@/utils/route-context";
import { api } from "@/redux/api";
import { useAuth, AUTH_LEVEL } from "@/hooks/use-auth";
import ContractCompanyRelationshipTable from "@/containers/contract/ContractCompanyRelationshipTable";
import ContractCompanyDetailModal, { getInitials } from "@/containers/contract/ContractCompanyDetailModal";
import ResponsiveSelect from "@components/ResponsiveSelect";
import { ENV } from "@config/env";
import message from "@/utils/globalMessage";

const EXCLUDED_RELATIONSHIP_STATUSES = new Set(["pending_invited", "pending_applied", "active"]);
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CONTRACTOR_VALUE_PREFIX = "contractor:";
const INVITE_VALUE_PREFIX = "invite:";
const CUSTOM_MESSAGE_MAX_LENGTH = 500;
const getRelationshipRows = (response) => {
  const rows = response?.data?.results || response?.data || response || [];
  return Array.isArray(rows) ? rows : [];
};

export default function FacilityContractCompanyPage() {
  const context = useSelector((state) => getFacilityContext(state));
  const isPC = useSelector((state) => state.App?.isPC ?? true);
  const canWrite = useAuth("facility_contract", AUTH_LEVEL.WRITE);
  const canFullAccess = useAuth("facility_contract", AUTH_LEVEL.FULL_ACCESS);
  const [companies, setCompanies] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteValue, setInviteValue] = useState();
  const [inviteSearch, setInviteSearch] = useState("");
  const [inviteEmails, setInviteEmails] = useState([]);
  const [customMessage, setCustomMessage] = useState("");
  const [previewContractor, setPreviewContractor] = useState(null);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const reloadRef = useRef();

  const openInvite = useCallback((reload) => {
    reloadRef.current = reload;
    setCompanies([]);
    setInviteValue(undefined);
    setInviteSearch("");
    setInviteEmails([]);
    setCustomMessage("");
    setPreviewContractor(null);
    setInviteOpen(true);
  }, []);

  useEffect(() => {
    if (!canWrite || !inviteOpen) return;
    setLoadingCompanies(true);
    Promise.all([
      api.getContractCompanyDirectory(),
      api.getFacilityContractRelationships(context.companyId),
    ])
      .then(([companyResponse, relationshipResponse]) => {
        const relationships = getRelationshipRows(relationshipResponse);
        const usedContractCompanyIds = new Set(
          relationships
            .filter((item) => EXCLUDED_RELATIONSHIP_STATUSES.has(item.status))
            .map((item) => String(item.contract_company ?? item.contract_company_id))
        );
        setCompanies((companyResponse.data || []).filter((item) => !usedContractCompanyIds.has(String(item.id))));
      })
      .catch(() => setCompanies([]))
      .finally(() => setLoadingCompanies(false));
  }, [canWrite, context.companyId, inviteOpen]);

  // The dropdown lists contractor names only (no email, no email search).
  // When the typed name matches no configured contractor, offer to email an
  // invitation to that company name instead.
  const inviteOptions = useMemo(() => {
    const keyword = inviteSearch.trim().toLowerCase();
    const matched = companies.filter(
      (item) => !keyword || String(item.name || "").toLowerCase().includes(keyword)
    );
    const options = matched.map((item) => ({
      value: `${CONTRACTOR_VALUE_PREFIX}${item.id}`,
      label: item.name,
      searchText: item.name || "",
    }));
    const normalizedSearch = inviteSearch.trim();
    if (normalizedSearch && matched.length === 0) {
      options.push({
        value: `${INVITE_VALUE_PREFIX}${normalizedSearch}`,
        label: `Send an invitation to ${normalizedSearch}`,
        searchText: normalizedSearch,
      });
    }
    return options;
  }, [companies, inviteSearch]);

  const isNewCompanyInvite = String(inviteValue || "").startsWith(INVITE_VALUE_PREFIX);
  const selectedContractor = useMemo(() => {
    if (!String(inviteValue || "").startsWith(CONTRACTOR_VALUE_PREFIX)) return null;
    const contractorId = String(inviteValue).slice(CONTRACTOR_VALUE_PREFIX.length);
    return companies.find((item) => String(item.id) === contractorId) || null;
  }, [companies, inviteValue]);

  // Pseudo relationship-shaped record so the shared detail modal renders the
  // exact same view as the contractor row click on this page.
  const previewDetail = useMemo(() => {
    if (!previewContractor) return null;
    return {
      contract_company: previewContractor.id,
      contract_company_name: previewContractor.name,
      contract_company_email: previewContractor.email,
      contract_company_logo: previewContractor.logo,
      contract_company_address: previewContractor.address,
      contract_company_description: previewContractor.description,
      contract_company_other_service: previewContractor.other_service,
      contract_company_contact_name: previewContractor.contact_name,
      contract_company_contact_phone: previewContractor.contact_phone,
      contract_company_contact_email: previewContractor.contact_email,
      service_categories: previewContractor.service_categories,
      certificates: previewContractor.certificates,
    };
  }, [previewContractor]);

  const submitInvite = useCallback(async () => {
    if (!context.companyId || !inviteValue) return;
    const emails = inviteEmails.map((item) => String(item).trim().toLowerCase()).filter(Boolean);
    const invalidEmail = emails.find((item) => !EMAIL_PATTERN.test(item));
    if (invalidEmail) {
      message.error(`Invalid email address: ${invalidEmail}`);
      return;
    }
    if (isNewCompanyInvite && emails.length === 0) {
      message.error("Enter at least one email address to send the invitation");
      return;
    }
    const trimmedMessage = customMessage.trim();
    setSubmitting(true);
    try {
      if (isNewCompanyInvite) {
        await api.inviteContractCompany(context.companyId, {
          emails,
          custom_message: trimmedMessage || undefined,
        });
        message.success("Invitation email sent");
      } else {
        const contractCompanyId = Number(String(inviteValue).slice(CONTRACTOR_VALUE_PREFIX.length));
        await api.inviteContractCompany(context.companyId, {
          contract_company: contractCompanyId,
          cc_emails: emails,
          custom_message: trimmedMessage || undefined,
        });
        message.success("Invitation sent");
      }
      setInviteOpen(false);
      reloadRef.current?.();
    } catch (error) {
      const responseData = error?.response?.data || {};
      const firstError = Object.values(responseData)[0];
      message.error(responseData.detail || (Array.isArray(firstError) ? firstError[0] : firstError) || "Unable to send invitation");
    } finally {
      setSubmitting(false);
    }
  }, [context.companyId, inviteValue, inviteEmails, customMessage, isNewCompanyInvite]);

  const registrationLink = (() => {
    if (!context.companyId) return "";
    const params = new URLSearchParams({ company_id: String(context.companyId) });
    return `${ENV.contractorPortalUrl}/contract/register?${params.toString()}`;
  })();
  const copyRegistrationLink = useCallback(async () => {
    if (!registrationLink) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(registrationLink);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = registrationLink;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      message.success("Registration link copied");
    } catch (_) {
      message.error("Unable to copy registration link");
    }
  }, [registrationLink]);

  const copyLinkRow = (
    <Tooltip title="Copy invitation link">
      <Button icon={<CopyOutlined />} onClick={copyRegistrationLink} aria-label="Copy invitation link">
        Copy invitation link
      </Button>
    </Tooltip>
  );
  const inviteContent = (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      {copyLinkRow}
      {loadingCompanies ? <Spin /> : (
        <>
          <div>
            <div style={{ marginBottom: 6, fontWeight: 500 }}>Contractor</div>
            <ResponsiveSelect
              autoFocus
              showSearch
              allowClear
              style={{ width: "100%" }}
              popupTitle="Select contractor"
              placeholder="Search contractor name"
              value={inviteValue}
              options={inviteOptions}
              optionFilterProp="searchText"
              filterOption={false}
              onSearch={setInviteSearch}
              onChange={(value) => setInviteValue(value)}
              notFoundContent="Type a company name to send an invitation"
            />
          </div>
          {selectedContractor ? (
            <a
              href="#contractor-details"
              onClick={(event) => {
                event.preventDefault();
                setPreviewContractor(selectedContractor);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 12px",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                color: "inherit",
              }}
            >
              <Avatar size={36} src={selectedContractor.logo}>
                {getInitials(selectedContractor.name)}
              </Avatar>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontWeight: 500, color: "#1677ff" }}>
                  {selectedContractor.name}
                </span>
                {selectedContractor.email ? (
                  <span style={{ display: "block", color: "#8c8c8c", fontSize: 12 }}>
                    {selectedContractor.email}
                  </span>
                ) : null}
              </span>
            </a>
          ) : null}
          <div>
            <div style={{ marginBottom: 6, fontWeight: 500 }}>
              {isNewCompanyInvite ? "Recipient email addresses" : "CC email addresses (optional)"}
              {isNewCompanyInvite ? <span style={{ color: "#dc2626" }}> *</span> : null}
            </div>
            <Select
              mode="tags"
              style={{ width: "100%" }}
              open={false}
              suffixIcon={null}
              tokenSeparators={[",", " ", ";"]}
              placeholder="Type an email and press Enter"
              value={inviteEmails}
              onChange={(values) => setInviteEmails(values)}
              options={[]}
            />
          </div>
          <div>
            <div style={{ marginBottom: 6, fontWeight: 500 }}>Custom message (optional)</div>
            <Input.TextArea
              rows={3}
              maxLength={CUSTOM_MESSAGE_MAX_LENGTH}
              showCount
              placeholder="Added as the first line of the invitation email"
              value={customMessage}
              onChange={(event) => setCustomMessage(event.target.value)}
            />
          </div>
        </>
      )}
    </Space>
  );
  const inviteDisabled = loadingCompanies || !inviteValue;
  const inviteActions = (
    <Space>
      <Button onClick={() => setInviteOpen(false)}>Cancel</Button>
      <Button type="primary" onClick={submitInvite} loading={submitting} disabled={inviteDisabled}>Invite</Button>
    </Space>
  );
  return (
    <>
      <ContractCompanyRelationshipTable
        title="Contractor Management"
        loadRelationships={() => api.getFacilityContractRelationships(context.companyId)}
        onInvite={canWrite ? openInvite : undefined}
        canWrite={canWrite}
        canFullAccess={canFullAccess}
        onApprove={api.approveContractCompany}
        onReject={api.rejectFacilityContractCompany}
        onRemove={api.deleteFacilityContractCompany}
      />
      {isPC ? (
        <Modal
          open={inviteOpen}
          title="Invite contractor"
          onCancel={() => setInviteOpen(false)}
          onOk={submitInvite}
          okText="Invite"
          okButtonProps={{ disabled: inviteDisabled }}
          confirmLoading={submitting}
        >
          {inviteContent}
        </Modal>
      ) : (
        <Drawer
          placement="bottom"
          height="72vh"
          title="Invite contractor"
          visible={inviteOpen}
          onClose={() => setInviteOpen(false)}
          destroyOnClose={false}
          className="responsive-picker-drawer"
          footer={inviteActions}
        >
          {inviteContent}
        </Drawer>
      )}
      <ContractCompanyDetailModal
        open={Boolean(previewContractor)}
        onClose={() => setPreviewContractor(null)}
        contractor={previewContractor ? {
          id: previewContractor.id,
          name: previewContractor.name,
          email: previewContractor.email,
          logo: previewContractor.logo,
        } : null}
        detail={previewDetail}
      />
    </>
  );
}
